import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { WorkflowGridService } from './workflow-grid.service';

/**
 * Angular 12 migration of workflow/workflowGrid.
 * Replicates: emptyGridJson?grid_id=106 (from workflow.js).
 */
@Component({
    selector: 'gtw-workflow-grid',
    templateUrl: './workflow-grid.component.html',
    styleUrls: ['./workflow-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkflowGridComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  filterParams: any = {};
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  title = 'Workflow Grid';
  private isInitialized = false;

  gridPayload: any = null;
  loading = true;
  errorMessage: string | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private workflowGridService: WorkflowGridService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.loadData();
    this.cdr.detectChanges();
  }

  loadData(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) {
      this.loading = false;
      this.errorMessage = 'Base URL not configured.';
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();
    this.workflowGridService.loadGridData(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data && data.callSuccess === '1') {
          this.gridPayload = data;
          this.errorMessage = null;
        } else {
          this.gridPayload = null;
          this.errorMessage = data?.errorMessage || 'Failed to load.';
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.gridPayload = null;
        this.errorMessage = err?.error?.errorMessage || err?.message || 'Error loading workflow grid.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  refresh(): void {
    this.loadData();
    this.requestGridUpdate.emit('refresh');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  get gridRows(): any[] {
    if (!this.gridPayload?.data) return [];
    const d = this.gridPayload.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.DATA)) return d.DATA;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }

  get gridColumns(): string[] {
    const rows = this.gridRows;
    if (rows.length === 0) return [];
    const first = rows[0];
    return typeof first === 'object' && first !== null ? Object.keys(first) : [];
  }
}
